// Compact composer to log the SAME activity against many customers at once
// (Customers list bulk action). Posts once to POST /crm/activities/bulk.
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Loader2, CalendarDays, Users2, Layers } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ACTIVITY_TYPES, REQUIRE_DUE, metaFor, activityToneCls } from "./activityMeta";
import VoiceNoteButton from "../../pages/quotes/panels/VoiceNoteButton";
import { ThemeToggle, useTheme } from "../../context/ThemeContext";

export default function BulkActivityModal({ personIds, onClose, onSaved }) {
  const lightOn = useTheme().theme === "light";
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    activity_type: "Call", subject: "", content: "", due_date: "",
    priority: "medium", assignee_id: "", completed: false,
  });
  const firstRef = useRef(null);
  const count = personIds.length;

  useEffect(() => { firstRef.current?.focus(); }, []);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!form.assignee_id && user?.id) setForm((f) => (f.assignee_id ? f : { ...f, assignee_id: user.id }));
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.subject.trim()) { setErr("Title is required"); return; }
    if (REQUIRE_DUE.has(form.activity_type) && !form.completed && !form.due_date) {
      setErr(`${form.activity_type} needs a due date — it schedules a task.`); return;
    }
    setSaving(true);
    try {
      const assignee = users.find((u) => u.id === form.assignee_id);
      const { data } = await api.post("/crm/activities/bulk", {
        person_ids: personIds,
        activity_type: form.activity_type,
        subject: form.subject.trim(),
        content: form.content.trim() || null,
        due_date: form.due_date || null,
        priority: form.priority,
        assignee_id: form.assignee_id || null,
        assignee_name: assignee ? (assignee.name || assignee.email) : null,
        completed: form.completed,
        create_task: !form.completed && !!form.due_date,
      });
      toast.success(`Logged to ${data.created} customer${data.created === 1 ? "" : "s"}${data.tasks ? ` · ${data.tasks} tasks` : ""}`);
      await onSaved?.();
    } catch (ex) {
      setErr(ex.response?.data?.detail || ex.message || "Failed to save");
      setSaving(false);
    }
  };

  return createPortal(
    <div className={`fixed inset-0 z-[200] grid place-items-center ${lightOn ? "bg-slate-900/45" : "bg-black/70"} p-4`} onClick={onClose} data-testid="bulk-activity-modal" data-talmia-theme={lightOn ? "light" : undefined}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} style={{ backgroundColor: lightOn ? "#FFFFFF" : "#0F1420" }}
        className={`w-full max-w-md max-h-[92vh] overflow-y-auto rounded-xl border ${lightOn ? "!border-slate-200" : "border-white/10"} shadow-2xl`}>
        <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
          <div className="flex items-center gap-2 text-purple-200 text-sm font-medium">
            <Layers className="w-4 h-4" /> Log activity · <span className="text-white">{count}</span> customers
          </div>
          <div className="flex items-center gap-1.5">
            <ThemeToggle className="!w-7 !h-7" />
            <button type="button" onClick={onClose} data-testid="bulk-activity-close" className="w-7 h-7 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Type</label>
            <div className="flex flex-wrap gap-1.5" data-testid="bulk-activity-type-picker">
              {ACTIVITY_TYPES.map((t) => {
                const Icon = metaFor(t).icon; const active = form.activity_type === t;
                return (
                  <button key={t} type="button" onClick={() => setForm((f) => ({ ...f, activity_type: t }))}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border transition-colors ${active ? activityToneCls(t) : "border-white/10 bg-white/[0.03] text-slate-400 hover:text-white"}`}>
                    <Icon className="w-3 h-3" /> {t}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Title</label>
            <input ref={firstRef} type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              placeholder="e.g. Q3 check-in call" maxLength={200} data-testid="bulk-activity-subject"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-purple-400/60 outline-none text-sm text-white placeholder:text-slate-500" />
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 flex items-center justify-between gap-2">Notes (optional)
              <VoiceNoteButton url="/ai/transcribe" testid="bulk-activity-voice" label="Speak it"
                onResult={({ raw, clean }) => setForm((f) => ({ ...f, content: [f.content, clean || raw].filter(Boolean).join("\n") }))} />
            </label>
            <textarea value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              placeholder="Applied to every selected customer…" rows={2} data-testid="bulk-activity-content"
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-purple-400/60 outline-none text-sm text-white placeholder:text-slate-500 resize-y" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 flex items-center gap-1.5">
                <CalendarDays className="w-3 h-3" /> Due date {REQUIRE_DUE.has(form.activity_type) && <span className="text-rose-400 normal-case">*</span>}
              </label>
              <input type="date" value={form.due_date} onChange={(e) => setForm((f) => ({ ...f, due_date: e.target.value }))}
                data-testid="bulk-activity-due-date" className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/10 focus:border-purple-400/60 outline-none text-sm text-white" />
            </div>
            <div>
              <label className="block text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5">Priority</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg bg-slate-900/70 border border-white/10 outline-none text-sm text-white">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10.5px] uppercase tracking-widest text-slate-500 font-mono mb-1.5 flex items-center gap-1.5"><Users2 className="w-3 h-3" /> Assign to</label>
            <select value={form.assignee_id} onChange={(e) => setForm((f) => ({ ...f, assignee_id: e.target.value }))}
              data-testid="bulk-activity-assignee" className="w-full px-3 py-2 rounded-lg bg-slate-900/70 border border-white/10 outline-none text-sm text-white">
              <option value="">Unassigned</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.id === user?.id ? " (You)" : ""}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer select-none">
            <input type="checkbox" checked={form.completed} onChange={(e) => setForm((f) => ({ ...f, completed: e.target.checked }))}
              data-testid="bulk-activity-done" className="w-4 h-4 rounded border-white/20 bg-white/5 accent-purple-500" />
            Mark as done <span className="text-slate-500 font-mono text-[10.5px]">— no tasks created</span>
          </label>
          {err && <div className="text-[12px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5" data-testid="bulk-activity-error">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-white/[0.06] bg-white/[0.02] flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-md text-sm text-slate-300 hover:text-white hover:bg-white/5">Cancel</button>
          <button type="submit" disabled={saving} data-testid="bulk-activity-submit"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium text-white bg-purple-600 hover:bg-purple-500 disabled:opacity-50">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Log to {count}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
